import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Award, BellRing, Download, LogOut, QrCode, RefreshCw, Search, Trash2 } from "lucide-react";
import { API } from "@/lib/api";

const STATI = {
  in_attesa: { label: "In attesa", cls: "bg-amber-100 text-amber-800" },
  pagato: { label: "Pagato", cls: "bg-emerald-100 text-emerald-800" },
  annullato: { label: "Annullato", cls: "bg-rose-100 text-rose-800" },
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rows, setRows] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [tab, setTab] = useState("iscritti");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [qrUrl, setQrUrl] = useState(() => window.location.origin);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/admin/registrations`, { headers }),
        fetch(`${API}/admin/waitlist`, { headers }),
      ]);
      if (r1.status === 401) {
        localStorage.removeItem("admin_token");
        setToken("");
        return;
      }
      setRows(await r1.json());
      setWaitlist(r2.ok ? await r2.json() : []);
    } catch {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Accesso negato");
      localStorage.setItem("admin_token", data.token);
      setToken(data.token);
      toast.success("Accesso effettuato");
    } catch (err) {
      toast.error(err.message || "Credenziali non valide");
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
  };

  const setStato = async (codice, stato) => {
    await fetch(`${API}/admin/registrations/${codice}`, {
      method: "PATCH",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ stato }),
    });
    load();
  };

  const remove = async (codice) => {
    if (!window.confirm(`Eliminare l'iscrizione ${codice}?`)) return;
    await fetch(`${API}/admin/registrations/${codice}`, { method: "DELETE", headers });
    toast.success("Iscrizione eliminata");
    load();
  };

  const downloadCert = async (codice) => {
    const res = await fetch(`${API}/admin/certificates/${codice}`, { headers });
    if (!res.ok) {
      toast.error("Errore nella generazione dell'attestato");
      return;
    }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `attestato-${codice}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportCsv = async () => {
    const res = await fetch(`${API}/admin/export`, { headers });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "iscritti-workshop.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const sendReminders = async () => {
    if (!window.confirm("Inviare l'email di promemoria a tutti gli iscritti (esclusi annullati)?")) return;
    setSendingReminders(true);
    try {
      const res = await fetch(`${API}/admin/send-reminders`, { method: "POST", headers });
      const data = await res.json();
      if (!res.ok) throw new Error();
      toast.success(`Promemoria inviati: ${data.sent}${data.failed ? ` · falliti: ${data.failed}` : ""}`);
    } catch {
      toast.error("Errore durante l'invio dei promemoria");
    } finally {
      setSendingReminders(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-petroldeep flex items-center justify-center px-6" data-testid="admin-login-page">
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-sm bg-ivory rounded-3xl p-8 shadow-2xl"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-mono font-semibold text-coral mb-2">Area riservata</p>
          <h1 className="font-serif text-2xl text-petrol font-medium mb-6">Accesso organizzatore</h1>
          <input
            data-testid="admin-email-input"
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" className="w-full mb-3 bg-white border border-petrol/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-coral/60"
          />
          <input
            data-testid="admin-password-input"
            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" className="w-full mb-5 bg-white border border-petrol/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-coral/60"
          />
          <button data-testid="admin-login-button" className="w-full bg-coral hover:bg-coralhover text-ivory font-semibold text-sm py-3.5 rounded-full transition-colors">
            Accedi
          </button>
          <a href="/" data-testid="admin-back-home-link" className="block text-center mt-4 text-xs text-muted2 hover:text-petrol transition-colors">← Torna alla pagina del corso</a>
        </motion.form>
      </div>
    );
  }

  const filtered = rows.filter((r) =>
    [r.nome, r.cognome, r.email, r.codice, r.professione].join(" ").toLowerCase().includes(search.toLowerCase())
  );
  const filteredWl = waitlist.filter((w) =>
    [w.nome, w.email, w.professione].join(" ").toLowerCase().includes(search.toLowerCase())
  );
  const pagati = rows.filter((r) => r.stato === "pagato").length;

  return (
    <div className="min-h-screen bg-cream/60 px-4 sm:px-8 py-8" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-mono font-semibold text-coral">Dashboard</p>
            <h1 className="font-serif text-2xl sm:text-3xl text-petrol font-medium">Iscrizioni al workshop</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-button" className="p-2.5 rounded-full bg-white border border-petrol/15 hover:border-petrol/40 transition-colors" aria-label="Aggiorna">
              <RefreshCw className={`w-4 h-4 text-petrol ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={sendReminders}
              disabled={sendingReminders}
              data-testid="admin-send-reminders-button"
              className="inline-flex items-center gap-2 bg-coral hover:bg-coralhover text-ivory text-sm font-semibold px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
            >
              <BellRing className="w-4 h-4" /> {sendingReminders ? "Invio..." : "Invia promemoria"}
            </button>
            <button onClick={exportCsv} data-testid="admin-export-csv-button" className="inline-flex items-center gap-2 bg-petrol hover:bg-petroldeep text-ivory text-sm font-semibold px-5 py-2.5 rounded-full transition-colors">
              <Download className="w-4 h-4" /> Esporta CSV
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="p-2.5 rounded-full bg-white border border-petrol/15 hover:border-coral/50 transition-colors" aria-label="Esci">
              <LogOut className="w-4 h-4 text-coral" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Stat testId="stat-total" label="Iscrizioni totali" value={rows.length} />
          <Stat testId="stat-paid" label="Pagamenti confermati" value={pagati} accent />
          <Stat testId="stat-pending" label="In attesa" value={rows.length - pagati - rows.filter((r) => r.stato === "annullato").length} />
          <Stat testId="stat-waitlist" label="Lista d'attesa" value={waitlist.length} />
        </div>

        <div className="bg-white border border-petrol/10 rounded-2xl p-5 mb-8 flex flex-col sm:flex-row items-center gap-4">
          <QrCode className="w-6 h-6 text-coral shrink-0" />
          <input
            data-testid="qr-url-input"
            value={qrUrl}
            onChange={(e) => setQrUrl(e.target.value)}
            className="flex-1 w-full bg-ivory border border-petrol/15 rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-coral/60"
            placeholder="URL di destinazione del QR"
          />
          <img src={`${API}/qr.png?url=${encodeURIComponent(qrUrl)}`} alt="Anteprima QR" className="w-16 h-16 rounded-lg border border-petrol/10" data-testid="qr-preview-image" />
          <a
            href={`${API}/qr.png?url=${encodeURIComponent(qrUrl)}`}
            download="qr-iscrizione-workshop.png"
            data-testid="admin-qr-download-button"
            className="inline-flex items-center gap-2 bg-coral hover:bg-coralhover text-ivory text-sm font-semibold px-5 py-2.5 rounded-full transition-colors whitespace-nowrap"
          >
            <Download className="w-4 h-4" /> Scarica QR
          </a>
        </div>

        <div className="flex items-center gap-2 mb-4" data-testid="admin-tabs">
          <button
            onClick={() => setTab("iscritti")}
            data-testid="tab-iscritti"
            className={`text-sm font-semibold px-5 py-2.5 rounded-full transition-colors ${tab === "iscritti" ? "bg-petrol text-ivory" : "bg-white border border-petrol/15 text-slate2 hover:border-petrol/40"}`}
          >
            Iscritti ({rows.length})
          </button>
          <button
            onClick={() => setTab("attesa")}
            data-testid="tab-attesa"
            className={`text-sm font-semibold px-5 py-2.5 rounded-full transition-colors ${tab === "attesa" ? "bg-petrol text-ivory" : "bg-white border border-petrol/15 text-slate2 hover:border-petrol/40"}`}
          >
            Lista d'attesa ({waitlist.length})
          </button>
        </div>

        <div className="relative mb-4">
          <Search className="w-4 h-4 text-muted2 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, email, codice, professione..."
            className="w-full bg-white border border-petrol/10 rounded-full pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-coral/50"
          />
        </div>

        {tab === "iscritti" ? (
          <div className="bg-white border border-petrol/10 rounded-2xl overflow-hidden" data-testid="admin-registrations-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted2 border-b border-petrol/10">
                    <th className="px-5 py-4">Codice</th>
                    <th className="px-5 py-4">Partecipante</th>
                    <th className="px-5 py-4">Professione</th>
                    <th className="px-5 py-4">Metodo</th>
                    <th className="px-5 py-4">Stato</th>
                    <th className="px-5 py-4 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.codice} className="border-b border-petrol/5 hover:bg-ivory/60 transition-colors" data-testid={`row-${r.codice}`}>
                      <td className="px-5 py-4 font-mono text-xs text-coral font-semibold">{r.codice}</td>
                      <td className="px-5 py-4">
                        <p className="font-medium text-ink">{r.nome} {r.cognome}</p>
                        <p className="text-xs text-muted2">{r.email} · {r.telefono}</p>
                      </td>
                      <td className="px-5 py-4 text-slate2">{r.professione}</td>
                      <td className="px-5 py-4 text-slate2">{r.metodo_pagamento === "bonifico" ? "Bonifico" : "Carta (SumUp)"}</td>
                      <td className="px-5 py-4">
                        <select
                          data-testid={`status-select-${r.codice}`}
                          value={r.stato}
                          onChange={(e) => setStato(r.codice, e.target.value)}
                          className={`text-xs font-semibold rounded-full px-3 py-1.5 border-0 cursor-pointer focus:outline-none ${STATI[r.stato]?.cls || ""}`}
                        >
                          {Object.entries(STATI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <button onClick={() => downloadCert(r.codice)} data-testid={`cert-${r.codice}`} className="p-2 rounded-lg hover:bg-tealight transition-colors" aria-label="Scarica attestato" title="Scarica attestato PDF">
                          <Award className="w-4 h-4 text-petrol" />
                        </button>
                        <button onClick={() => remove(r.codice)} data-testid={`delete-${r.codice}`} className="p-2 rounded-lg hover:bg-rose-50 transition-colors" aria-label="Elimina">
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-12 text-center text-muted2 text-sm" data-testid="empty-state">Nessuna iscrizione trovata</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-petrol/10 rounded-2xl overflow-hidden" data-testid="admin-waitlist-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted2 border-b border-petrol/10">
                    <th className="px-5 py-4">Nome</th>
                    <th className="px-5 py-4">Email</th>
                    <th className="px-5 py-4">Professione</th>
                    <th className="px-5 py-4">Data richiesta</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWl.map((w) => (
                    <tr key={w.id} className="border-b border-petrol/5 hover:bg-ivory/60 transition-colors" data-testid={`wl-row-${w.id}`}>
                      <td className="px-5 py-4 font-medium text-ink">{w.nome}</td>
                      <td className="px-5 py-4 text-slate2">{w.email}</td>
                      <td className="px-5 py-4 text-slate2">{w.professione || "—"}</td>
                      <td className="px-5 py-4 text-muted2 text-xs font-mono">{new Date(w.created_at).toLocaleDateString("it-IT")}</td>
                    </tr>
                  ))}
                  {filteredWl.length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-12 text-center text-muted2 text-sm" data-testid="waitlist-empty-state">Nessun contatto in lista d'attesa</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, accent, testId }) => (
  <div className="bg-white border border-petrol/10 rounded-2xl px-5 py-4" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-muted2">{label}</p>
    <p className={`font-serif text-3xl font-medium mt-1 ${accent ? "text-coral" : "text-petrol"}`}>{value}</p>
  </div>
);
